import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { SidePanel } from 'designSystem/components/SidePanel'
import { SIDE_PANEL_REM } from 'designSystem/components/sidePanelWidth'
import { useMediaQuery } from 'designSystem/components/useMediaQuery'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { ArtifactPanel } from 'features/knowledgeDocumentViewer/artifacts/ArtifactPanel'
import { useAgenticChatArtifactPanel } from 'features/knowledgeDocumentViewer/artifacts/useAgenticChatArtifactPanel'
import { useArtifactActions } from 'features/knowledgeDocumentViewer/artifacts/useArtifactActions'
import { DocumentPreviewPanel } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/DocumentPreviewPanel'
import { FloatingDocumentPreviewOverlay } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/FloatingDocumentPreviewOverlay'
import { useFloatingDocumentPreview } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/useFloatingDocumentPreview'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useAgenticChatPreview } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useAgenticChatPreview'
import { useDocChatHeaderState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatHeaderState'
import { findLatestArtifactEvent } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type { Artifact } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isDocChatSearchResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  InspectionPreviewResult,
  InspectionViewContext,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import {
  inspectionViewScope,
  writeInspectionViewContext,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionViewContextStore'
import type { TrendPreview } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import { trendPreviewFromRef } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import {
  setTrendPreview as writeTrendPreview,
  showTrendPreview,
  syncTrendPreviewView,
  trendPreviewScope,
  useTrendPreview,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreviewStore'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import type { AgenticChatLayout } from 'features/knowledgeDocumentViewer/types'
import { citedQuotesFragment } from 'features/knowledgeDocumentViewer/utils/citedQuotesFragment'
import type { EmbeddedTrendView } from 'features/timeSeries/computed/EmbeddedWorkbenchContext'
import { PictureInPicture2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { AgenticChatColumn } from './AgenticChatColumn'
import { ChatPage } from './agenticChatPageStyles'
import { FloatingChatWindow } from './FloatingChatWindow'
import { ImagePreviewPanel } from './ImagePreviewPanel'
import { InspectionPreviewPanel } from './InspectionPreviewPanel'
import {
  type ImagePreview,
  type RevisionComparePreview,
} from './previewDocumentHandler'
import { RevisionComparePreviewPanel } from './RevisionComparePreviewPanel'
import { TrendPreviewPanel } from './TrendPreviewPanel'

const CHAT_PREVIEW_OVERLAY_QUERY = '(max-width: 1023px)'

const AGENTIC_CHAT_PREVIEW_WIDTH_KEY =
  'documentSearch.agenticChatPreview.width:v1'

const REVISION_PREVIEW_WIDTH = 60 * SIDE_PANEL_REM

type LocalPreview =
  | { kind: 'inspection'; value: InspectionPreviewResult }
  | { kind: 'image'; value: ImagePreview }
  | { kind: 'revision'; value: RevisionComparePreview }
  | null

const FloatingEmptyState = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.8125rem;
  text-align: center;
`

const UndockButtonSlot = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  z-index: 5;
`

const DockedChatSurface = styled.div`
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

export function AgenticChatPage() {
  const {
    handleSearchKeyDown,
    handleSearchSubmit,
    isActive,
    onSearchStateChange,
    searchMode,
    searchState,
    siteId,
    tabId,
  } = useAdvancedSearchUX()
  const { t } = useLingui()
  const usePreviewOverlay = useMediaQuery(CHAT_PREVIEW_OVERLAY_QUERY)
  const { isLoadingDocuments, rawSearchData } = useDocumentSearchData()
  const { onAddRecentDocument, onOpenNewTab, onSelectDocument } =
    useSearchActions()
  const {
    handleClosePreview,
    handleDismissCitedQuotes,
    handlePreviewDocument,
    isLoadingPreview,
    isPreviewOpen,
    previewCitedQuotes,
    previewDocument,
    previewPage,
  } = useAgenticChatPreview()
  const [localPreview, setLocalPreview] = useState<LocalPreview>(null)
  const inspectionScope = inspectionViewScope(siteId, tabId)
  useEffect(() => {
    setLocalPreview(null)
    writeInspectionViewContext(inspectionScope, null)
  }, [inspectionScope])
  const handleInspectionViewChange = useCallback(
    (context: InspectionViewContext | null) =>
      writeInspectionViewContext(inspectionScope, context),
    [inspectionScope]
  )
  useEffect(
    () => () => writeInspectionViewContext(inspectionScope, null),
    [inspectionScope]
  )
  const trendScope = trendPreviewScope(tabId)
  const trendPreview = useTrendPreview(trendScope)
  const setTrendPreview = useCallback(
    (preview: TrendPreview | null) => writeTrendPreview(trendScope, preview),
    [trendScope]
  )
  const handleTrendViewChange = useCallback(
    (view: EmbeddedTrendView) => syncTrendPreviewView(trendScope, view),
    [trendScope]
  )
  const { artifactId, closeArtifact, isArtifactOpen } =
    useAgenticChatArtifactPanel()
  const { openInTab: openArtifactInTab } = useArtifactActions()
  // Previews and the artifact never co-exist: opening an artifact clears the
  // local previews and the document preview, which live outside the artifact.
  useEffect(() => {
    if (artifactId) {
      setLocalPreview(null)
      setTrendPreview(null)
      handleClosePreview()
    }
  }, [artifactId, handleClosePreview, setTrendPreview])
  const result = isDocChatSearchResult(rawSearchData)
    ? rawSearchData
    : undefined
  const conversation =
    searchState.docChat?.siteId === siteId
      ? searchState.docChat.conversation
      : undefined
  const {
    turns,
    canSubmit,
    isGenerating,
    onDocChatKeyDown,
    onDocChatSubmit,
    onDocChatStop,
  } = useDocChatHeaderState({
    conversation,
    result,
    isLoadingDocuments,
    query: searchState.query,
    siteId,
    tabId,
    searchMode,
    onSearchSubmit: handleSearchSubmit,
    onSearchKeyDown: handleSearchKeyDown,
    onSearchStateChange,
  })

  const latestArtifactEvent = useMemo(
    () =>
      artifactId
        ? findLatestArtifactEvent(conversation, artifactId)
        : undefined,
    [artifactId, conversation]
  )

  const isFloating = searchState.chatLayout === 'floating'
  const setChatLayout = useCallback(
    (chatLayout: AgenticChatLayout) => onSearchStateChange({ chatLayout }),
    [onSearchStateChange]
  )

  const handlePreviewDocumentOnly = useCallback(
    (document: DocumentTableRow, pageNumber = 1, citedQuotes?: string[]) => {
      setLocalPreview(null)
      setTrendPreview(null)
      closeArtifact()
      handlePreviewDocument(document, pageNumber, citedQuotes)
    },
    [closeArtifact, handlePreviewDocument, setTrendPreview]
  )

  const handlePreviewImage = useCallback(
    (image: ImagePreview) => {
      handleClosePreview()
      closeArtifact()
      setTrendPreview(null)
      setLocalPreview({ kind: 'image', value: image })
    },
    [closeArtifact, handleClosePreview, setTrendPreview]
  )

  const handlePreviewRevision = useCallback(
    (revision: RevisionComparePreview) => {
      handleClosePreview()
      closeArtifact()
      setTrendPreview(null)
      setLocalPreview({ kind: 'revision', value: revision })
    },
    [closeArtifact, handleClosePreview, setTrendPreview]
  )

  const handlePreviewInspection = useCallback(
    (result: InspectionPreviewResult) => {
      handleClosePreview()
      closeArtifact()
      setTrendPreview(null)
      setLocalPreview({ kind: 'inspection', value: result })
    },
    [handleClosePreview, closeArtifact, setTrendPreview]
  )

  const seenInspectionEvents = useRef<Set<string> | null>(null)
  useEffect(() => {
    const events = turns.flatMap((turn) =>
      turn.events.filter(
        (event) =>
          event.type === 'response' && Boolean(event.inspection_results?.length)
      )
    )
    if (seenInspectionEvents.current === null) {
      seenInspectionEvents.current = new Set(events.map((event) => event.id))
      return
    }
    for (const event of events) {
      if (event.type !== 'response') continue
      if (seenInspectionEvents.current.has(event.id)) continue
      seenInspectionEvents.current.add(event.id)
      const result = event.inspection_results?.at(-1)
      if (result) handlePreviewInspection(result)
    }
  }, [turns, handlePreviewInspection])

  const handlePreviewTrend = useCallback(
    (trend: TrendPreview) => {
      handleClosePreview()
      closeArtifact()
      setLocalPreview(null)
      showTrendPreview(trendScope, trend)
    },
    [closeArtifact, handleClosePreview, trendScope]
  )

  // Seeding the ref set on first render keeps hydrated history from popping
  // the trend panel open on mount.
  const seenTrendEventIdsRef = useRef<Set<string> | null>(null)
  useEffect(() => {
    const trendEvents = turns.flatMap((turn) =>
      turn.events.flatMap((event) =>
        event.type === 'response' && event.time_series?.trend
          ? [{ id: event.id, trend: event.time_series.trend }]
          : []
      )
    )
    if (seenTrendEventIdsRef.current === null) {
      seenTrendEventIdsRef.current = new Set(
        trendEvents.map((entry) => entry.id)
      )
      return
    }
    const seen = seenTrendEventIdsRef.current
    trendEvents.forEach((entry) => {
      if (seen.has(entry.id)) return
      seen.add(entry.id)
      handlePreviewTrend(trendPreviewFromRef(entry.trend))
    })
  }, [turns, handlePreviewTrend])

  const handleCloseAnyPreview = useCallback(() => {
    setLocalPreview(null)
    setTrendPreview(null)
    handleClosePreview()
    closeArtifact()
  }, [closeArtifact, handleClosePreview, setTrendPreview])

  const handleClosePreviewOnly = useCallback(() => {
    setLocalPreview(null)
    handleClosePreview()
  }, [handleClosePreview])

  const handleOpenArtifactInTab = useCallback(
    (artifact: Artifact) =>
      openArtifactInTab({
        artifact_id: artifact.artifact_id,
        title: artifact.title,
      }),
    [openArtifactInTab]
  )

  const handleDocumentNavigate = useCallback(
    (
      document: DocumentTableRow,
      openInNewTab: boolean,
      _isLinkedDocsMode: boolean,
      target?: { pageNumber?: number; citedQuotes?: string[] }
    ) => {
      onAddRecentDocument(document)
      const options = {
        documentId: document.id,
        pageNumber: target?.pageNumber,
        searchOriginQuery: searchState.query,
        fragmentHighlight:
          target?.pageNumber !== undefined
            ? citedQuotesFragment(target.pageNumber, target.citedQuotes)
            : undefined,
      }
      if (openInNewTab) {
        onOpenNewTab(options)
        return
      }
      onSelectDocument(options)
    },
    [onAddRecentDocument, onOpenNewTab, onSelectDocument, searchState.query]
  )

  const hasConversation = turns.length > 0
  const isPreviewVisible =
    isPreviewOpen ||
    localPreview !== null ||
    trendPreview !== null ||
    isArtifactOpen
  const { usesFloatingPreview, showFloatingPreview } =
    useFloatingDocumentPreview({
      preferOverlay: usePreviewOverlay,
      isActive,
      isOpen: isPreviewVisible,
      onInactive: 'close',
      onClose: handleCloseAnyPreview,
    })

  const chatColumn = (
    <AgenticChatColumn
      hasConversation={hasConversation}
      onPreviewDocument={handlePreviewDocumentOnly}
      onPreviewTrend={handlePreviewTrend}
      onPreviewImage={handlePreviewImage}
      onPreviewInspection={handlePreviewInspection}
      onPreviewRevision={handlePreviewRevision}
      onNavigateDocument={handleDocumentNavigate}
      onSearchKeyDown={onDocChatKeyDown}
      onSearchSubmit={onDocChatSubmit}
      onStop={onDocChatStop}
      isGenerating={isGenerating}
      canSubmit={canSubmit}
      conversationId={conversation?.id ?? null}
      turns={turns}
    />
  )
  const renderPreviewContent = () => {
    if (trendPreview) {
      return (
        <TrendPreviewPanel
          trend={trendPreview}
          siteId={siteId}
          onClose={handleCloseAnyPreview}
          onViewChange={handleTrendViewChange}
        />
      )
    }
    switch (localPreview?.kind) {
      case 'inspection':
        return (
          <InspectionPreviewPanel
            key={JSON.stringify(localPreview.value)}
            result={localPreview.value}
            onViewContextChange={handleInspectionViewChange}
            onClose={handleCloseAnyPreview}
          />
        )
      case 'revision':
        return (
          <RevisionComparePreviewPanel
            revision={localPreview.value}
            siteId={siteId}
            onClose={handleCloseAnyPreview}
          />
        )
      case 'image':
        return (
          <ImagePreviewPanel
            image={localPreview.value}
            onClose={handleCloseAnyPreview}
          />
        )
      default:
        break
    }
    if (isPreviewOpen) {
      return (
        <DocumentPreviewPanel
          key={`${previewDocument?.id ?? 'loading'}-${previewPage}`}
          document={previewDocument}
          initialPage={previewPage}
          citedQuotes={previewCitedQuotes}
          onDismissCitedQuotes={handleDismissCitedQuotes}
          isLinkedDocsMode={false}
          isLoading={isLoadingPreview}
          isOpen
          panelBorder='none'
          onClose={handleClosePreviewOnly}
          onDocumentNavigate={handleDocumentNavigate}
          onSwitchPreviewDocument={handlePreviewDocumentOnly}
        />
      )
    }
    if (artifactId) {
      return (
        <ArtifactPanel
          key={artifactId}
          artifactId={artifactId}
          siteId={siteId}
          latestEvent={latestArtifactEvent}
          onClose={handleCloseAnyPreview}
          onOpenInTab={handleOpenArtifactInTab}
        />
      )
    }
    return null
  }
  const previewContent = renderPreviewContent()

  if (usesFloatingPreview) {
    return (
      <>
        {chatColumn}
        {showFloatingPreview ? (
          <FloatingDocumentPreviewOverlay onClose={handleCloseAnyPreview}>
            {previewContent}
          </FloatingDocumentPreviewOverlay>
        ) : null}
      </>
    )
  }

  return (
    <ChatPage>
      <SidePanel
        variant='inset'
        motion='smooth'
        minMainContentWidth={16 * SIDE_PANEL_REM}
        openWidthRatio={0.5}
        persistWidthKey={AGENTIC_CHAT_PREVIEW_WIDTH_KEY}
        expandPanel={isFloating}
        open={isPreviewVisible}
        preferredWidth={
          localPreview?.kind === 'revision' ? REVISION_PREVIEW_WIDTH : undefined
        }
        onClose={handleCloseAnyPreview}
        pageContent={
          isFloating ? (
            <FloatingEmptyState>
              <Trans>Previews from the chat open here at full width.</Trans>
            </FloatingEmptyState>
          ) : (
            <DockedChatSurface>
              {chatColumn}
              {hasConversation ? (
                <UndockButtonSlot>
                  <Button
                    variant='ghost'
                    size='icon-sm'
                    type='button'
                    aria-label={t`Float the chat over the preview`}
                    title={t`Float the chat over the preview`}
                    onClick={() => setChatLayout('floating')}>
                    <PictureInPicture2 size={14} />
                  </Button>
                </UndockButtonSlot>
              ) : null}
            </DockedChatSurface>
          )
        }
        sidePanelContent={previewContent}
      />
      {isFloating ? (
        <FloatingChatWindow onDock={() => setChatLayout('docked')}>
          {chatColumn}
        </FloatingChatWindow>
      ) : null}
    </ChatPage>
  )
}
